import React from 'react';
import { TableRow, TableCell, Tooltip, Stack, Typography, CircularProgress, Box, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { VisibilityChip } from './VisibilityChip';
import { SourceTypeChip } from './SourceTypeChip';
import { DocumentActionButtons } from './DocumentActionButtons';
import type { DocumentTableRowProps } from '../types';
import type { Document } from '../../../../api';

/**
 * Type guard to check if document has source_type field
 * SearchResult doesn't have source_type, but Document does
 */
function hasSourceType(doc: Document | any): doc is Document {
  return 'source_type' in doc;
}

/**
 * DocumentTableRow - Memoized table row component
 *
 * Only re-renders when its specific document changes.
 * Uses extracted cell components for clean separation of concerns.
 *
 * @param doc - Document to display
 * @param canEditDocument - Function to check if user can edit this document
 * @param canDeleteDocument - Function to check if user can delete this document
 * @param onEdit - Callback when edit button is clicked
 * @param onDelete - Callback when delete button is clicked
 * @param onView - Callback when view button is clicked
 * @param onDownload - Callback when download button is clicked
 * @param renderOwner - Function to render owner cell
 * @param renderSharedGroups - Function to render shared groups cell
 * @param renderSharedUsers - Function to render shared users cell
 */
export const DocumentTableRow = React.memo<DocumentTableRowProps>(({
  doc,
  canEditDocument,
  canDeleteDocument,
  onEdit,
  onDelete,
  onView,
  onDownload,
  renderOwner,
  renderSharedGroups,
  renderSharedUsers,
  onReprocess,
  currentUserId,
  currentUserGroupIds,
  selectable,
  selected,
  onToggleSelection
}) => {
  const { t } = useTranslation();
  // Type-safe check for source_type field
  const sourceType = hasSourceType(doc) ? doc.source_type : undefined;
  // OCR reviewer hint: the list endpoint attaches metadata.ocr_review for
  // documents whose scanned pages read badly (low recognizer confidence or a
  // second OCR pass); shown as a small warning chip next to the title.
  const docMetadata = 'metadata' in doc
    ? (doc.metadata as {
        ocr_review?: {
          needs_review?: boolean;
          min_p10?: number | null;
          low_pages?: number[];
          page_count?: number;
          second_pass?: boolean;
          engine?: string | null;
          skipped?: { ocr_pages?: number; budget?: number } | null;
        };
      } | undefined)
    : undefined;
  const ocrReview = docMetadata?.ocr_review;
  const ocrNeedsReview = Boolean(ocrReview?.needs_review);
  const engineLabel = ocrReview?.engine === 'easyocr' ? 'EasyOCR' : ocrReview?.engine === 'rapidocr' ? 'RapidOCR' : (ocrReview?.engine ?? 'OCR');
  const lowPages = ocrReview?.low_pages ?? [];
  const confidence = ocrReview?.min_p10 !== null && ocrReview?.min_p10 !== undefined ? Math.round(ocrReview.min_p10 * 100) : null;

  // Indexing state is an icon-only column; the header carries the legend
  // and every icon explains itself on hover.
  const indexing = doc as { status?: string; indexed_at?: string | null; error_message?: string | null; failure_reason?: string | null };
  const reprocessInfo = (docMetadata as { reprocess?: { attempts?: number; max?: number; exhausted?: boolean } } | undefined)?.reprocess;
  const attemptsLine = reprocessInfo && (reprocessInfo.max ?? 0) > 0
    ? t('documents.table.status.attempts', { attempts: reprocessInfo.attempts ?? 0, max: reprocessInfo.max })
    : null;
  const retryExhaustedHint = t('documents.table.reindexExhausted', { max: reprocessInfo?.max ?? 0 });
  const status = indexing.status ?? 'completed';
  const statusIconSx = { fontSize: 20, cursor: 'help', display: 'block' } as const;
  const showStatus = status === 'pending' || status === 'processing' || status === 'failed';
  // A failure the backend names by a stable key gets a sentence; anything
  // else is shown as it came.
  const failureText = indexing.failure_reason === 'stuck_in_processing'
    ? t('documents.table.status.stuck')
    : indexing.error_message
      ? t(`documents.table.status.reasons.${indexing.error_message}`, { defaultValue: indexing.error_message })
      : t('documents.table.status.failedUnknown');
  const indexedAt = indexing.indexed_at ? new Date(indexing.indexed_at) : null;
  const indexedHint = indexedAt && !Number.isNaN(indexedAt.getTime())
    ? t('documents.table.status.indexedAt', { date: indexedAt.toLocaleString() })
    : null;

  // Structured hover text: what was found, why it matters, what to do.
  const ocrHint = ocrReview?.skipped ? (
    <Stack spacing={0.75}>
      <Typography variant="subtitle2" component="div">{t('documents.table.ocrHint.skippedTitle')}</Typography>
      <Typography variant="body2" component="div">
        {t('documents.table.ocrHint.skipped', { pages: ocrReview.skipped.ocr_pages ?? '', budget: ocrReview.skipped.budget ?? '' })}
      </Typography>
      <Typography variant="body2" component="div" sx={{ opacity: 0.85 }}>{t('documents.table.ocrHint.adviceSkipped')}</Typography>
    </Stack>
  ) : (
    <Stack spacing={0.75}>
      <Typography variant="subtitle2" component="div">{t('documents.table.ocrHint.title')}</Typography>
      {lowPages.length > 0 && confidence !== null && (
        <Typography variant="body2" component="div">
          {t(lowPages.length === 1 ? 'documents.table.ocrHint.lowConfidenceOne' : 'documents.table.ocrHint.lowConfidenceMany', {
            pages: lowPages.join(', '),
            count: ocrReview?.page_count ?? lowPages.length,
            confidence,
            engine: engineLabel,
          })}
        </Typography>
      )}
      {ocrReview?.second_pass && (
        <Typography variant="body2" component="div">{t('documents.table.ocrHint.secondPass')}</Typography>
      )}
      <Typography variant="body2" component="div" sx={{ opacity: 0.85 }}>{t('documents.table.ocrHint.adviceReview')}</Typography>
    </Stack>
  );
  const isSap = sourceType === 'sap';
  const canEdit = canEditDocument(doc);
  const canDelete = canDeleteDocument ? canDeleteDocument(doc) : canEdit;

  return (
    <TableRow selected={selected}>
      {onToggleSelection && (
        <TableCell padding="checkbox">
          {/* No checkbox on a row the user may not change: it could not be
              part of any bulk action, and the server would refuse it. */}
          {selectable && (
            <Checkbox
              size="small"
              checked={Boolean(selected)}
              onChange={() => onToggleSelection(doc.document_id ?? '')}
              aria-label={t('documents.bulk.selectOne', { title: doc.title ?? '' })}
            />
          )}
        </TableCell>
      )}
      <TableCell component="th" scope="row" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <Tooltip arrow title={doc.title ?? ''} enterDelay={300}>
          <span>{doc.title}</span>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', px: 1 }}>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          {status === 'pending' && (
            <Tooltip arrow title={t('documents.table.status.pendingHint')}>
              <HourglassEmptyOutlinedIcon sx={{ ...statusIconSx, color: 'text.disabled' }} aria-label={t('documents.table.status.pending')} />
            </Tooltip>
          )}
          {status === 'processing' && (
            <Tooltip arrow title={t('documents.table.status.processingHint')}>
              <Box sx={{ display: 'inline-flex', width: 20, height: 20, alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={14} thickness={5} color="info" aria-label={t('documents.table.status.processing')} />
              </Box>
            </Tooltip>
          )}
          {status === 'failed' && (
            <Tooltip
              arrow
              slotProps={{ tooltip: { sx: { maxWidth: 380, p: 1.5 } } }}
              title={
                <Stack spacing={0.75}>
                  <Typography variant="subtitle2" component="div">{t('documents.table.status.failedTitle')}</Typography>
                  <Typography variant="body2" component="div">{failureText}</Typography>
                  {attemptsLine && (
                    <Typography variant="body2" component="div">{attemptsLine}</Typography>
                  )}
                  <Typography variant="body2" component="div" sx={{ opacity: 0.85 }}>
                    {reprocessInfo?.exhausted ? retryExhaustedHint : t('documents.table.status.failedAdvice')}
                  </Typography>
                </Stack>
              }
            >
              <ErrorOutlineIcon sx={{ ...statusIconSx, color: 'error.main' }} aria-label={t('documents.table.status.failed')} />
            </Tooltip>
          )}
          {!showStatus && (
            <Tooltip arrow title={indexedHint ?? t('documents.table.status.completedHint')}>
              <CheckCircleOutlineIcon sx={{ ...statusIconSx, color: 'success.main' }} aria-label={t('documents.table.status.completed')} />
            </Tooltip>
          )}
          {ocrNeedsReview && (
            <Tooltip arrow title={ocrHint} slotProps={{ tooltip: { sx: { maxWidth: 380, p: 1.5 } } }}>
              <WarningAmberOutlinedIcon sx={{ ...statusIconSx, color: 'warning.main' }} aria-label={t('documents.table.ocrReview')} />
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <VisibilityChip visibility={doc.visibility || 'private'} doc={doc} currentUserId={currentUserId} currentUserGroupIds={currentUserGroupIds} />
      </TableCell>
      <TableCell>
        {renderOwner(doc)}
      </TableCell>
      <TableCell>
        {renderSharedGroups(doc)}
      </TableCell>
      <TableCell>
        {renderSharedUsers(doc)}
      </TableCell>
      <TableCell>
        <SourceTypeChip
          sourceType={sourceType}
          isSap={isSap}
        />
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <DocumentActionButtons
          doc={doc}
          canEdit={canEdit}
          canDelete={canDelete}
          onView={onView}
          onDownload={onDownload}
          onEdit={onEdit}
          onDelete={onDelete}
          onReprocess={onReprocess}
          status={status}
          retryExhausted={Boolean(reprocessInfo?.exhausted)}
          retryExhaustedHint={retryExhaustedHint}
        />
      </TableCell>
    </TableRow>
  );
});

DocumentTableRow.displayName = 'DocumentTableRow';
